/**
 * Hooks the desktop sync engine into the data service.
 *
 * The pattern: each entity has a tiny `(payload) => Promise<{ id }>`
 * handler that knows how to translate an outbox payload into a
 * concrete IDataService call. We register handlers once at boot;
 * the engine drains the outbox in order whenever the network is up.
 *
 * **Coverage today** — every entity wired through `useResilientMutation`
 * in apps/erp-web/src/hooks/ has a matching handler here:
 *   - clients, suppliers, articles
 *   - article_categories, article_families, article_brands
 *   - representatives (incl. linkClient / unlinkClient custom ops)
 *   - payments, recurring_payments (incl. payment-category ops)
 *   - bank_accounts (treasury)
 *   - stock_movements (incl. warehouse + inventory custom ops)
 *   - checks (incl. updateState + transitionState custom ops)
 *   - sales_documents, purchase_documents (incl. markAsInvoiced)
 *   - caisse_sessions (open/close), caisse_movements
 *
 * **To wire a new entity:**
 *   1. Add the bucket name to `SyncableEntity` in
 *      libs/native/interfaces/src/sync.ts.
 *   2. Migrate the React hook from `useMutation` to
 *      `useResilientMutation` (same shape — the wrapper handles the
 *      offline path, optimistic cache write, and retry).
 *   3. Add a handler for each (entity, op) below — usually three lines:
 *      destructure payload, call the data service method, return the
 *      server id (for `create`).
 */
import type { ITauriSyncEngine } from '@erp/native/tauri-adapter';
import type { IDataService } from '@erp/data-access/interfaces';

/**
 * Postgres unique-violation = 23505 (also reported as the SQLSTATE code
 * by Supabase). We catch it specifically because that's the only error
 * the strip-and-retry recovery is safe for; anything else (RLS, NOT NULL,
 * connection failure) should bubble through to the normal retry loop.
 */
function isDuplicateKeyError(err: unknown): boolean {
  if (!err || typeof err !== 'object') return false;
  const e = err as { code?: unknown; message?: unknown };
  if (e.code === '23505') return true;
  if (typeof e.message === 'string' && /duplicate key|23505/i.test(e.message)) return true;
  return false;
}

function mentionsCode(err: unknown): boolean {
  if (!err || typeof err !== 'object') return false;
  const m = ((err as { message?: unknown }).message ?? '').toString();
  return /code/i.test(m) && !/barcode/i.test(m);
}

function mentionsBarcode(err: unknown): boolean {
  if (!err || typeof err !== 'object') return false;
  const m = ((err as { message?: unknown }).message ?? '').toString();
  return /barcode/i.test(m);
}

/**
 * Generic "create with auto-assigned-key recovery" — used by clients,
 * suppliers, articles. If the server returns a unique-violation on the
 * `code` column, retry with `code` stripped so the BEFORE-INSERT
 * trigger picks the next sequential value via `consume_entity_code`.
 *
 * We loop up to MAX_CODE_RETRIES because the server-side counter can be
 * BEHIND the actual data (e.g. articles imported manually without
 * advancing the sequence). Each consume bumps the counter by one, so
 * after a handful of attempts it always passes the highest existing
 * code and the insert succeeds. Without the loop, a tenant whose
 * counter is N codes behind would see a stuck outbox entry forever.
 */
const MAX_CODE_RETRIES = 8;

async function createWithCodeRetry<T extends { id: string }>(
  payload: Record<string, unknown>,
  doCreate: (input: Record<string, unknown>) => Promise<T>,
): Promise<{ serverId: string }> {
  let lastErr: unknown;
  let attempt: Record<string, unknown> = payload;
  for (let i = 0; i <= MAX_CODE_RETRIES; i++) {
    try {
      const created = await doCreate(attempt);
      return { serverId: created.id };
    } catch (err) {
      lastErr = err;
      if (!isDuplicateKeyError(err)) throw err;
      // First retry: drop the offline-computed code so the server
      // assigns a fresh one. Subsequent retries: keep stripping and
      // re-firing — each call advances the server counter by one,
      // eventually clearing whatever existing rows the counter was
      // behind.
      attempt = { ...attempt };
      delete attempt.code;
    }
  }
  throw lastErr ?? new Error('createWithCodeRetry: exhausted retries');
}

export function registerSyncHandlers(engine: ITauriSyncEngine, ds: IDataService) {
  // Clients ---------------------------------------------------------
  engine.registerHandler('clients', 'create', async (entry) =>
    createWithCodeRetry(entry.payload as Record<string, unknown>, (input) =>
      ds.clients.create(input as never) as Promise<{ id: string }>,
    ),
  );
  engine.registerHandler('clients', 'update', async (entry) => {
    const p = entry.payload as { id: string; input: unknown };
    await ds.clients.update(p.id, p.input as never);
  });
  engine.registerHandler('clients', 'delete', async (entry) => {
    // Payload is the raw id string (matches the React hook signature).
    await ds.clients.delete(entry.payload as string);
  });

  // Representatives -------------------------------------------------
  engine.registerHandler('representatives', 'create', async (entry) => {
    const created = await ds.representatives.create(entry.payload as never);
    return { serverId: (created as { id: string }).id };
  });
  engine.registerHandler('representatives', 'update', async (entry) => {
    const p = entry.payload as { id: string; input: unknown };
    await ds.representatives.update(p.id, p.input as never);
  });
  engine.registerHandler('representatives', 'delete', async (entry) => {
    await ds.representatives.delete(entry.payload as string);
  });
  engine.registerHandler('representatives', 'custom', async (entry) => {
    const p = entry.payload as { clientId: string; representativeId: string };
    if (entry.method === 'linkClient') {
      await ds.representatives.linkClient(p.clientId, p.representativeId);
      return;
    }
    if (entry.method === 'unlinkClient') {
      await ds.representatives.unlinkClient(p.clientId, p.representativeId);
      return;
    }
    throw new Error(`Unknown representatives method: ${entry.method}`);
  });

  // Suppliers -------------------------------------------------------
  engine.registerHandler('suppliers', 'create', async (entry) =>
    createWithCodeRetry(entry.payload as Record<string, unknown>, (input) =>
      ds.suppliers.create(input as never) as Promise<{ id: string }>,
    ),
  );
  engine.registerHandler('suppliers', 'update', async (entry) => {
    const p = entry.payload as { id: string; input: unknown };
    await ds.suppliers.update(p.id, p.input as never);
  });
  engine.registerHandler('suppliers', 'delete', async (entry) => {
    await ds.suppliers.delete(entry.payload as string);
  });

  // Articles --------------------------------------------------------
  engine.registerHandler('articles', 'create', async (entry) => {
    const payload = entry.payload as Record<string, unknown>;
    let lastErr: unknown;
    let attempt: Record<string, unknown> = payload;
    // Loop up to MAX_CODE_RETRIES so we beat a server-side counter that's
    // behind the actual data (each `consume_entity_code` call bumps the
    // counter by one — eventually it passes every existing row).
    for (let i = 0; i <= MAX_CODE_RETRIES; i++) {
      try {
        const created = await ds.articles.create(attempt as never);
        return { serverId: (created as { id: string }).id };
      } catch (err) {
        lastErr = err;
        if (!isDuplicateKeyError(err)) throw err;
        attempt = { ...attempt };
        // Drop whichever field the error mentioned. On the first hit
        // we usually know exactly which key collided; on later loops
        // both code and barcode are already gone (idempotent).
        if (mentionsBarcode(err) || !mentionsCode(err)) delete attempt.barcode;
        if (mentionsCode(err) || !mentionsBarcode(err)) delete attempt.code;
        // Defensive: if we didn't strip anything, kill both and let
        // the server fully auto-assign.
        if (attempt.code === payload.code && attempt.barcode === payload.barcode) {
          delete attempt.code;
          delete attempt.barcode;
        }
      }
    }
    throw lastErr ?? new Error('articles.create: exhausted retries');
  });
  engine.registerHandler('articles', 'update', async (entry) => {
    const p = entry.payload as { id: string; input: unknown };
    await ds.articles.update(p.id, p.input as never);
  });
  engine.registerHandler('articles', 'delete', async (entry) => {
    await ds.articles.delete(entry.payload as string);
  });

  // Article taxonomy: categories / families / brands ----------------
  engine.registerHandler('article_categories', 'create', async (entry) => {
    const created = await ds.articleCategories.create(entry.payload as never);
    return { serverId: (created as { id: string }).id };
  });
  engine.registerHandler('article_categories', 'update', async (entry) => {
    const p = entry.payload as { id: string; input: unknown };
    await ds.articleCategories.update(p.id, p.input as never);
  });
  engine.registerHandler('article_categories', 'delete', async (entry) => {
    await ds.articleCategories.delete(entry.payload as string);
  });
  engine.registerHandler('article_families', 'create', async (entry) => {
    const created = await ds.articleFamilies.create(entry.payload as string);
    return { serverId: (created as { id: string }).id };
  });
  engine.registerHandler('article_families', 'update', async (entry) => {
    const p = entry.payload as { id: string; name: string };
    await ds.articleFamilies.update(p.id, { name: p.name });
  });
  engine.registerHandler('article_families', 'delete', async (entry) => {
    await ds.articleFamilies.delete(entry.payload as string);
  });
  engine.registerHandler('article_brands', 'create', async (entry) => {
    const created = await ds.articleBrands.create(entry.payload as never);
    return { serverId: (created as { id: string }).id };
  });
  engine.registerHandler('article_brands', 'update', async (entry) => {
    const p = entry.payload as { id: string; name?: string; imageUrl?: string };
    const { id, ...rest } = p;
    await ds.articleBrands.update(id, rest);
  });
  engine.registerHandler('article_brands', 'delete', async (entry) => {
    await ds.articleBrands.delete(entry.payload as string);
  });

  // Payments --------------------------------------------------------
  engine.registerHandler('payments', 'create', async (entry) => {
    const created = await ds.payments.create(entry.payload as never);
    return { serverId: (created as { id: string }).id };
  });
  engine.registerHandler('payments', 'delete', async (entry) => {
    await ds.payments.delete(entry.payload as string);
  });

  // Recurring payments ----------------------------------------------
  engine.registerHandler('recurring_payments', 'create', async (entry) => {
    const created = await ds.recurringPayments.create(entry.payload as never);
    return { serverId: (created as { id: string }).id };
  });
  engine.registerHandler('recurring_payments', 'update', async (entry) => {
    const p = entry.payload as { id: string; input: unknown };
    await ds.recurringPayments.update(p.id, p.input as never);
  });
  engine.registerHandler('recurring_payments', 'delete', async (entry) => {
    await ds.recurringPayments.delete(entry.payload as string);
  });
  engine.registerHandler('recurring_payments', 'custom', async (entry) => {
    const payload = entry.payload as Record<string, unknown>;
    if (entry.method === 'createCategory') {
      const created = await ds.paymentCategories.create(payload as never);
      return { serverId: (created as { id: string }).id };
    }
    if (entry.method === 'updateCategory') {
      const p = payload as unknown as { id: string; input: unknown };
      await ds.paymentCategories.update(p.id, p.input as never);
      return;
    }
    if (entry.method === 'deleteCategory') {
      await ds.paymentCategories.delete(payload as unknown as string);
      return;
    }
    throw new Error(`Unknown recurring_payments method: ${entry.method}`);
  });

  // Bank accounts ---------------------------------------------------
  engine.registerHandler('bank_accounts', 'create', async (entry) => {
    const created = await ds.treasury.createBankAccount(entry.payload as never);
    return { serverId: (created as { id: string }).id };
  });
  engine.registerHandler('bank_accounts', 'update', async (entry) => {
    const p = entry.payload as { id: string; input: unknown };
    await ds.treasury.updateBankAccount(p.id, p.input as never);
  });
  engine.registerHandler('bank_accounts', 'delete', async (entry) => {
    await ds.treasury.deleteBankAccount(entry.payload as string);
  });

  // Stock movements + warehouses + inventories ---------------------
  engine.registerHandler('stock_movements', 'create', async (entry) => {
    const created = await ds.stock.createMovement(entry.payload as never);
    return { serverId: (created as { id: string }).id };
  });
  engine.registerHandler('stock_movements', 'custom', async (entry) => {
    const payload = entry.payload as Record<string, unknown>;
    if (entry.method === 'createWarehouse') {
      const created = await ds.stock.createWarehouse(payload as never);
      return { serverId: (created as { id: string }).id };
    }
    if (entry.method === 'updateWarehouse') {
      const p = payload as unknown as { id: string; input: unknown };
      await ds.stock.updateWarehouse(p.id, p.input as never);
      return;
    }
    if (entry.method === 'deleteWarehouse') {
      await ds.stock.deleteWarehouse(payload as unknown as string);
      return;
    }
    if (entry.method === 'createInventory') {
      const created = await ds.stock.createInventory(payload as never);
      return { serverId: (created as { id: string }).id };
    }
    if (entry.method === 'validateInventory') {
      await ds.stock.validateInventory(payload as unknown as string);
      return;
    }
    throw new Error(`Unknown stock_movements method: ${entry.method}`);
  });

  // Checks / traites ------------------------------------------------
  engine.registerHandler('checks', 'create', async (entry) => {
    const created = await ds.checks.create(entry.payload as never);
    return { serverId: (created as { id: string }).id };
  });
  engine.registerHandler('checks', 'update', async (entry) => {
    const p = entry.payload as { id: string; input: unknown };
    await ds.checks.update(p.id, p.input as never);
  });
  engine.registerHandler('checks', 'delete', async (entry) => {
    await ds.checks.delete(entry.payload as string);
  });
  engine.registerHandler('checks', 'custom', async (entry) => {
    const payload = entry.payload as Record<string, unknown>;
    if (entry.method === 'updateState') {
      const p = payload as unknown as { id: string; newState: string };
      await ds.checks.updateState(p.id, p.newState);
      return;
    }
    if (entry.method === 'transitionState') {
      const p = payload as unknown as {
        id: string;
        toState: string;
        options?: Record<string, unknown>;
      };
      await ds.checks.transitionState(p.id, p.toState as never, p.options as never);
      return;
    }
    throw new Error(`Unknown checks method: ${entry.method}`);
  });

  // Sales documents -------------------------------------------------
  engine.registerHandler('sales_documents', 'create', async (entry) => {
    const created = await ds.sales.createDocument(entry.payload as never);
    return { serverId: (created as { id: string }).id };
  });
  engine.registerHandler('sales_documents', 'update', async (entry) => {
    const p = entry.payload as { id: string; input: unknown };
    await ds.sales.updateDocument(p.id, p.input as never);
  });
  engine.registerHandler('sales_documents', 'delete', async (entry) => {
    await ds.sales.deleteDocument(entry.payload as string);
  });
  engine.registerHandler('sales_documents', 'custom', async (entry) => {
    if (entry.method === 'markAsInvoiced') {
      await ds.sales.markAsInvoiced(entry.payload as string);
      return;
    }
    throw new Error(`Unknown sales_documents method: ${entry.method}`);
  });

  // Purchase documents ----------------------------------------------
  engine.registerHandler('purchase_documents', 'create', async (entry) => {
    const created = await ds.purchases.createDocument(entry.payload as never);
    return { serverId: (created as { id: string }).id };
  });
  engine.registerHandler('purchase_documents', 'update', async (entry) => {
    const p = entry.payload as { id: string; input: unknown };
    await ds.purchases.updateDocument(p.id, p.input as never);
  });
  engine.registerHandler('purchase_documents', 'delete', async (entry) => {
    await ds.purchases.deleteDocument(entry.payload as string);
  });
  engine.registerHandler('purchase_documents', 'custom', async (entry) => {
    if (entry.method === 'markAsInvoiced') {
      await ds.purchases.markAsInvoiced(entry.payload as string);
      return;
    }
    throw new Error(`Unknown purchase_documents method: ${entry.method}`);
  });

  // Caisse ----------------------------------------------------------
  // The three writes the cashier needs while offline. The 'custom' op
  // for openSession / closeSession dispatches by `entry.method` since
  // both share the same entity but have different repository signatures.
  engine.registerHandler('caisse_sessions', 'custom', async (entry) => {
    const payload = entry.payload as Record<string, unknown>;
    if (entry.method === 'openSession') {
      const created = await ds.caisse.openSession(payload as never);
      return { serverId: (created as { id: string }).id };
    }
    if (entry.method === 'closeSession') {
      const updated = await ds.caisse.closeSession(payload as never);
      return { serverId: (updated as { id: string }).id };
    }
    throw new Error(`Unknown caisse session method: ${entry.method}`);
  });
  engine.registerHandler('caisse_movements', 'create', async (entry) => {
    const created = await ds.caisse.createMovement(entry.payload as never);
    return { serverId: (created as { id: string }).id };
  });

  // Sales / purchases / payments — wired the same way once their UI
  // starts queuing mutations through the engine. See the module docs
  // in libs/native/CLAUDE.md for the migration recipe.

  // Treasury transactions ------------------------------------------
  engine.registerHandler('bank_transactions', 'create', async (entry) => {
    const created = await ds.treasury.createTransaction(entry.payload as never);
    return { serverId: (created as { id: string }).id };
  });
  engine.registerHandler('bank_transactions', 'update', async (entry) => {
    const p = entry.payload as { id: string; input: unknown };
    await ds.treasury.updateTransaction(p.id, p.input as never);
  });
  engine.registerHandler('bank_transactions', 'delete', async (entry) => {
    await ds.treasury.deleteTransaction(entry.payload as string);
  });
  engine.registerHandler('bank_transactions', 'custom', async (entry) => {
    const p = entry.payload as Record<string, unknown>;
    if (entry.method === 'reconcile') {
      await ds.treasury.reconcileTransactions(p.ids as string[], Boolean(p.reconciled));
      return;
    }
    if (entry.method === 'recalculateBalance') {
      await ds.treasury.recalculateBalance(p.accountId as string);
      return;
    }
    throw new Error(`Unknown bank_transactions method: ${entry.method}`);
  });

  // Stock vehicle documents ----------------------------------------
  engine.registerHandler('stock_exit_documents', 'create', async (entry) => {
    const created = await ds.stock.createStockExitDocument(entry.payload as never);
    return { serverId: (created as { id: string }).id };
  });

  // Withholding -----------------------------------------------------
  engine.registerHandler('withholding_certificates', 'create', async (entry) => {
    const created = await ds.withholding.createCertificate(entry.payload as never);
    return { serverId: (created as { id: string }).id };
  });
  engine.registerHandler('withholding_certificates', 'update', async (entry) => {
    const p = entry.payload as { id: string; input: unknown };
    await ds.withholding.updateCertificate(p.id, p.input as never);
  });
  engine.registerHandler('withholding_certificates', 'delete', async (entry) => {
    await ds.withholding.deleteCertificate(entry.payload as string);
  });
  engine.registerHandler('withholding_declarations', 'create', async (entry) => {
    const created = await ds.withholding.createDeclaration(entry.payload as never);
    return { serverId: (created as { id: string }).id };
  });
  engine.registerHandler('withholding_declarations', 'delete', async (entry) => {
    await ds.withholding.deleteDeclaration(entry.payload as string);
  });
  engine.registerHandler('withholding_declarations', 'custom', async (entry) => {
    const p = entry.payload as { id: string; xmlContent: string; declarantMatricule: string };
    if (entry.method === 'markExported') {
      await ds.withholding.markDeclarationExported(p.id, p.xmlContent, p.declarantMatricule);
      return;
    }
    throw new Error(`Unknown withholding_declarations method: ${entry.method}`);
  });

  // PDF templates ---------------------------------------------------
  engine.registerHandler('pdf_templates', 'create', async (entry) => {
    const created = await ds.pdfTemplates.create(entry.payload as never);
    return { serverId: (created as { id: string }).id };
  });
  engine.registerHandler('pdf_templates', 'update', async (entry) => {
    const p = entry.payload as { id: string; input: unknown };
    await ds.pdfTemplates.update(p.id, p.input as never);
  });
  engine.registerHandler('pdf_templates', 'delete', async (entry) => {
    await ds.pdfTemplates.delete(entry.payload as string);
  });

  // Resources metadata ---------------------------------------------
  engine.registerHandler('resource_folders', 'create', async (entry) => {
    const created = await ds.resources.createFolder(entry.payload as never);
    return { serverId: (created as { id: string }).id };
  });
  engine.registerHandler('resource_folders', 'update', async (entry) => {
    const p = entry.payload as { id: string; input: unknown };
    await ds.resources.updateFolder(p.id, p.input as never);
  });
  engine.registerHandler('resource_folders', 'delete', async (entry) => {
    await ds.resources.deleteFolder(entry.payload as string);
  });
  engine.registerHandler('resource_files', 'create', async (entry) => {
    const created = await ds.resources.createFile(entry.payload as never);
    return { serverId: (created as { id: string }).id };
  });
  engine.registerHandler('resource_files', 'update', async (entry) => {
    const p = entry.payload as { id: string; input: unknown };
    await ds.resources.updateFile(p.id, p.input as never);
  });
  engine.registerHandler('resource_files', 'delete', async (entry) => {
    await ds.resources.deleteFile(entry.payload as string);
  });

  // Formations ------------------------------------------------------
  engine.registerHandler('formation_pages', 'create', async (entry) => {
    const created = await ds.formationPages.create(entry.payload as never);
    return { serverId: (created as { id: string }).id };
  });
  engine.registerHandler('formation_pages', 'update', async (entry) => {
    const p = entry.payload as { id: string; input: unknown };
    await ds.formationPages.update(p.id, p.input as never);
  });
  engine.registerHandler('formation_pages', 'delete', async (entry) => {
    await ds.formationPages.delete(entry.payload as string);
  });
  engine.registerHandler('formation_pages', 'custom', async (entry) => {
    if (entry.method === 'reorder') {
      await ds.formationPages.reorder(entry.payload as string[]);
      return;
    }
    throw new Error(`Unknown formation_pages method: ${entry.method}`);
  });

  // Storefront categories -------------------------------------------
  engine.registerHandler('storefront_categories', 'create', async (entry) => {
    const created = await ds.storefrontCategories.create(entry.payload as never);
    return { serverId: (created as { id: string }).id };
  });
  engine.registerHandler('storefront_categories', 'update', async (entry) => {
    const p = entry.payload as { id: string; input: unknown };
    await ds.storefrontCategories.update(p.id, p.input as never);
  });
  engine.registerHandler('storefront_categories', 'delete', async (entry) => {
    await ds.storefrontCategories.delete(entry.payload as string);
  });
  engine.registerHandler('storefront_categories', 'custom', async (entry) => {
    if (entry.method === 'reorder') {
      await ds.storefrontCategories.reorder(entry.payload as string[]);
      return;
    }
    throw new Error(`Unknown storefront_categories method: ${entry.method}`);
  });

  // Storefront products ---------------------------------------------
  engine.registerHandler('storefront_products', 'create', async (entry) => {
    const created = await ds.storefrontProducts.create(entry.payload as never);
    return { serverId: (created as { id: string }).id };
  });
  engine.registerHandler('storefront_products', 'update', async (entry) => {
    const p = entry.payload as { id: string; input: unknown };
    await ds.storefrontProducts.update(p.id, p.input as never);
  });
  engine.registerHandler('storefront_products', 'delete', async (entry) => {
    await ds.storefrontProducts.delete(entry.payload as string);
  });

  // Store banners ---------------------------------------------------
  engine.registerHandler('store_banners', 'create', async (entry) => {
    const created = await ds.storeBanners.create(entry.payload as never);
    return { serverId: (created as { id: string }).id };
  });
  engine.registerHandler('store_banners', 'update', async (entry) => {
    const p = entry.payload as { id: string; input: unknown };
    await ds.storeBanners.update(p.id, p.input as never);
  });
  engine.registerHandler('store_banners', 'delete', async (entry) => {
    await ds.storeBanners.delete(entry.payload as string);
  });

  // Store pages (update only — page rows are seeded server-side per
  // tenant; the dashboard only edits content / SEO / visibility) ----
  engine.registerHandler('store_pages', 'update', async (entry) => {
    const p = entry.payload as { id: string; input: unknown };
    await ds.storePages.update(p.id, p.input as never);
  });

  // Store checkout fields -------------------------------------------
  engine.registerHandler('store_checkout_fields', 'create', async (entry) => {
    const created = await ds.storeCheckoutFields.create(entry.payload as never);
    return { serverId: (created as { id: string }).id };
  });
  engine.registerHandler('store_checkout_fields', 'update', async (entry) => {
    const p = entry.payload as { id: string; input: unknown };
    await ds.storeCheckoutFields.update(p.id, p.input as never);
  });
  engine.registerHandler('store_checkout_fields', 'delete', async (entry) => {
    await ds.storeCheckoutFields.delete(entry.payload as string);
  });
}
